import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { KTSVG, SimpleAlert } from '../../../../../_mexablock/helpers';
import { confirmStatus, getStatuses } from '../../../../service/operacion/Portabilidad';

type Props = {
  show: boolean;
  id: number;
  targetStatusName: string;
  selectedRows: any[];
  handleClose: (updateGrid: boolean) => void;
};

const RULES_BY_NAME: Record<string, string[]> = {
  'Pendiente': ['En ABD', 'No procesada'],
  'PEN': ['En ABD', 'No procesada'],
  'En ABD': ['Portada', 'No procesada'],
  'ABD': ['Portada', 'No procesada'],
  'Portada': [],
  'EXT': [],
  'Rechazada': [],
  'REC': [],
  'Error': [],
  'ERR': [],
  'No procesada': [],
  'NOP': [],
};

interface IneligibleInfo {
  row: any;
  reason: string;
}

const ConfirmModal: React.FC<Props> = ({ show, id, targetStatusName, selectedRows, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState(false);
  const [extraConfirm, setExtraConfirm] = useState(false);

  useEffect(() => {
    if (show) {
      setComment('');
      setCommentError(false);
      setExtraConfirm(false);
      getStatuses()
        .then((data: any) => {
          const list = Array.isArray(data) ? data : (data?.data || []);
          setStatuses(list);
        })
        .catch((err) => console.error('Error fetching portability statuses:', err));

    }
  }, [show, targetStatusName]);
  
  const foundStatus = statuses.find(
    (s) => s.name?.toLowerCase().trim() === targetStatusName?.toLowerCase().trim() ||
           s.code?.toLowerCase().trim() === targetStatusName?.toLowerCase().trim()
  );
  const targetStatusId = foundStatus?.id || null;

  // Determine targets
  const rows = id > 0
    ? (selectedRows.find(r => r.id === id) ? [selectedRows.find(r => r.id === id)] : selectedRows.filter(r => r.id === id))
    : selectedRows;
  
  const eligible: any[] = [];
  const ineligible: IneligibleInfo[] = [];

  rows.forEach((r: any) => {
    const currentName = r?.status?.name || r?.status?.code;
    const isNoProcesada = currentName === 'No procesada' || currentName === 'NOP' || r?.status_id === 42;

    if (isNoProcesada) {
      ineligible.push({
        row: r,
        reason: 'En estatus "No procesada" (debe capturarse con "Enviar a portar")',
      });
      return;
    }

    if (!currentName || !RULES_BY_NAME[currentName]?.includes(targetStatusName)) {
      ineligible.push({
        row: r,
        reason: `Estatus actual "${currentName || 'Desconocido'}" no permite cambiar a "${targetStatusName}"`,
      });
      return;
    }

    const requiresPhoneAndNip = ['Pendiente', 'En ABD', 'Portada'].includes(targetStatusName);
    const hasPhone = Boolean(
      r?.phone_number &&
      String(r.phone_number).trim() !== '' &&
      String(r.phone_number).trim() !== 'Pendiente'
    );
    const hasNip = Boolean(r?.nip && String(r.nip).trim() !== '');

    if (requiresPhoneAndNip && (!hasPhone || !hasNip)) {
      ineligible.push({
        row: r,
        reason: 'Sin número a portar o NIP asignados',
      });
      return;
    }

    eligible.push(r);
  });

  const isRejection = targetStatusName === 'No procesada';
  const needsExtra = isRejection || targetStatusName === 'Portada' || eligible.length > 20;

  const handlerConfirmStatus = async () => {
    if (!targetStatusId) {
      SimpleAlert('Error al resolver el ID del estatus objetivo. Intente más tarde.', 3000, 'error');
      return;
    }
    
    if (isRejection && !comment.trim()) {
      setCommentError(true);
      return;
    }
    
    setLoading(true);
    let successCount = 0;
    let lastError = '';

    for (const r of eligible) {
      try {
        await confirmStatus(r.id, targetStatusId, comment);
        successCount++;
      } catch (err: any) {
        lastError = err?.response?.data?.message || err?.message || '';
        console.error('Error changing status for id', r.id, err);
      }
    }

    setLoading(false);
    if (successCount > 0) {
      const displayStatus = isRejection ? 'No procesada (Rechazado)' : targetStatusName;
      if (ineligible.length > 0) {
        SimpleAlert(
          `Se actualizaron exitosamente ${successCount} registro(s) a ${displayStatus}. Se omitieron ${ineligible.length} registro(s) sin número/NIP o incompatibles.`,
          5000,
          'success'
        );
      } else {
        SimpleAlert(`Se actualizaron ${successCount} registro(s) a ${displayStatus}`, 3000, 'success');
      }
      handleClose(true);
    } else {
      SimpleAlert(lastError || `No se pudo actualizar el estatus. Revise la información o intente nuevamente.`, 4000, 'error');
    }
  };

  return (
    <Modal
      id="kt_modal_active_user"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered mw-500px"
      show={show}
      onHide={() => handleClose(false)}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            {isRejection ? (
              <>Rechazar solicitud <span className="text-danger">(Regresar a No procesada)</span></>
            ) : (
              <>Cambiar estatus a <span className="text-primary">{targetStatusName}</span></>
            )}
          </h2>
          <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => handleClose(false)}>
            <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
          </div>
        </div>
        <div className="modal-body">
          <p className="text-muted fs-6 mb-4">
            <span className="fw-bold text-dark">{eligible.length}</span> de <span className="fw-bold text-dark">{rows.length}</span> registro(s) son elegibles para este cambio.
          </p>

          {ineligible.length > 0 && eligible.length > 0 && (
            <div className="alert alert-info d-flex align-items-center p-4 mb-4">
              <span className="svg-icon svg-icon-2hx svg-icon-info me-3">
                <i className="bi bi-info-circle-fill fs-3 text-info"></i>
              </span>
              <div className="fs-7 text-gray-800">
                Al cambiar el estatus de varios registros, los que no tengan número a portar o NIP asignados no arrojarán error de validación; solo se aplicará el cambio a los <strong>{eligible.length} registro(s)</strong> que sí se pueden cambiar.
              </div>
            </div>
          )}

          {ineligible.length > 0 && (
            <div className="alert alert-warning p-4 mb-4">
              <div className="fs-7 text-dark">
                <span className="fw-bold">{ineligible.length} registro(s) se omitirán automáticamente:</span>
                <ul className="mb-0 mt-2 ps-4">
                  {ineligible.slice(0, 5).map((item, idx) => (
                    <li key={idx}>
                      {item.row?.customer?.first_name || 'Sin nombre'} ({item.row?.status?.name || 'S/E'}) &mdash; <span className="text-danger fw-semibold">{item.reason}</span>
                    </li>
                  ))}
                </ul>
                {ineligible.length > 5 && <div className="mt-1">... y {ineligible.length - 5} más.</div>}
              </div>
            </div>
          )}

          {eligible.length === 0 && rows.length > 0 && (
            <div className="alert alert-danger p-4 mb-4 fs-7">
              <div className="fw-bold mb-1">No se puede realizar el cambio:</div>
              Ninguno de los registros seleccionados cuenta con número a portar o NIP asignados (o su estatus actual no es compatible) para cambiar a "{targetStatusName}".
            </div>
          )}

          {(targetStatusName === 'Portada' || isRejection) && (
            <div className="alert alert-primary p-4 mb-4">
              <div className="fs-7 text-dark">
                {isRejection
                  ? `Este rechazo regresará la solicitud a "No procesada" y enviará un correo de notificación con el motivo a ${eligible.length} cliente(s).`
                  : `Este cambio enviará un correo de notificación de éxito a ${eligible.length} cliente(s).`}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="form-label fs-7 fw-semibold text-gray-600">
              {isRejection ? 'Motivo del rechazo (obligatorio)' : 'Comentario (opcional)'}
            </label>
            <textarea 
              className="form-control" 
              rows={3} 
              placeholder={isRejection ? 'Describe la razón por la cual se rechaza la solicitud' : 'Agrega una nota para el historial'}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                setCommentError(false);
              }}
            />
            {commentError && <div className="text-danger fs-8 mt-1">Agrega un motivo antes de confirmar.</div>}
          </div>

          {needsExtra && (
            <div className="d-flex align-items-center mb-4">
              <input 
                className="form-check-input me-3" 
                type="checkbox" 
                id="extraConfirm"
                checked={extraConfirm}
                onChange={(e) => setExtraConfirm(e.target.checked)}
              />
              <label className="form-check-label fs-7 text-gray-600" htmlFor="extraConfirm">
                Entiendo que esta acción notificará a los clientes y aplicará el cambio de estatus.
              </label>
            </div>
          )}

          <div className="text-end pt-4">
            <button className="btn btn-light me-3" onClick={() => handleClose(false)} disabled={loading}>
              Cancelar
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handlerConfirmStatus} 
              disabled={loading || eligible.length === 0 || (needsExtra && !extraConfirm)}
            >
              {!loading ? 'Confirmar' : <span className="indicator-progress" style={{display: 'block'}}>Espere... <span className="spinner-border spinner-border-sm align-middle ms-2"></span></span>}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { ConfirmModal };
